import { useMemo } from 'react';

import {
  AuthorFragmentFragment,
  HeaderMenuItemFragmentFragment,
  ImageFragmentFragment,
  LinkFragmentFragment,
} from '../../__generated__/graphql';
import { useBlogIndexQueryString } from '../../layout/Header/state/hooks';
import AppImage from '../AppImage';
import BreadCrumbs from '../BreadCrumbs';
import { Heading2, Paragraph } from '../Text';

import style from './entry-header.module.css';

interface EntryHeaderProps {
  author?: AuthorFragmentFragment;
  authorClassName?: string;
  date?: Date | null;
  featureImage?: ImageFragmentFragment | null;
  featuredImageContainerClassName?: string;
  headerMenuItems: HeaderMenuItemFragmentFragment[] | null;
  title: string;
}

const EntryHeader = ({
  author,
  authorClassName,
  date,
  featureImage,
  featuredImageContainerClassName,
  headerMenuItems,
  title,
}: EntryHeaderProps) => {
  const blogMenuItem = useMemo(() => {
    let blogLink: LinkFragmentFragment | null = null;

    for (const item of headerMenuItems ?? []) {
      for (const segment of item.navSegments?.segments ?? []) {
        for (const link of segment?.links ?? []) {
          if (link?.link?.title?.toLowerCase() === 'blog') {
            blogLink = link.link;
            break;
          }
        }

        if (blogLink) {
          break;
        }
      }

      if (blogLink) {
        break;
      }
    }

    return blogLink;
  }, [headerMenuItems]);

  const blogIndexQueryString = useBlogIndexQueryString();

  const breadCrumbMenuItems: LinkFragmentFragment[] = useMemo(() => {
    if (!blogMenuItem) {
      return [];
    }

    const blogUrl = blogMenuItem.url ?? '';

    return [
      {
        ...blogMenuItem,
        url: blogIndexQueryString ? `${blogUrl}?${blogIndexQueryString}` : blogUrl,
      },
      {
        title,
        url: '/', // the last item in the breadcrumb does not have a link
      },
    ];
  }, [blogIndexQueryString, blogMenuItem, title]);

  return (
    <>
      {/* intentionally always rendering the container div so the grid layout is predictable */}
      <div className={style.titleContainer}>
        <BreadCrumbs
          breadCrumbMenuItems={breadCrumbMenuItems}
          className={style.breadCrumbs}
        />

        {title && (
          <div className={style.titleWrapper}>
            <Heading2
              className={style.title}
              fontSizeOverride={48}
            >
              {title}
            </Heading2>
          </div>
        )}
      </div>

      {/* intentionally always rendering the container div so the grid layout is predictable */}
      <div className={style.imageContainer}>
        {featureImage && (
          <div className={style.featureImage}>
            <AppImage
              className={featuredImageContainerClassName}
              fallbackHeight={460}
              fallbackWidth={770}
              fetchPriority="high"
              image={featureImage}
              priority
            />
          </div>
        )}
      </div>

      {/* intentionally always rendering the container div so the grid layout is predictable */}
      <div
        className={style.author}
        rel="author"
      >
        {(!!author || !!date) && (
          <div className={authorClassName}>
            {author?.avatar && (
              <AppImage
                className={style.avatar}
                fallbackHeight={48}
                fallbackWidth={48}
                image={{
                  node: {
                    altText: author.name,
                    id: author.avatar.url ?? '',
                    link: author.avatar.url,
                    mediaDetails: {
                      height: author.avatar.height ?? 48,
                      width: author.avatar.width ?? 48,
                    },
                    sourceUrl: author.avatar.url,
                  },
                }}
              />
            )}
            <div className={style.authorInfo}>
              {author?.name && (
                <Paragraph
                  fontSize={12}
                  fontWeight={700}
                >
                  {author.name}
                </Paragraph>
              )}
              {author?.description && (
                <Paragraph fontSize={12}>
                  {author.description}
                </Paragraph>
              )}
              {date && (
                <time className={style.date}>
                  {date.toLocaleDateString(undefined, {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </time>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EntryHeader;
